"use client"
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { MapPin, Info, Save, Loader2 } from "lucide-react"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import toast from "react-hot-toast"

export default function AttendanceSettings() {
  const [attendanceRadius, setAttendanceRadius] = useState(1000)
  const [studentBusTrackingEnabled, setStudentBusTrackingEnabled] = useState(true)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [schoolId, setSchoolId] = useState(null)
  const [error, setError] = useState("")

  useEffect(() => {
    fetchSchoolData()
  }, [])

  const fetchSchoolData = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      
      // Get school details to get school ID
      const schoolResponse = await axiosInstance.get(
        API_ENDPOINTS.schoolAbout,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (schoolResponse.data.status === "success") {
        const school = schoolResponse.data.data.school
        setSchoolId(school._id)

        // Fetch school settings
        const settingsResponse = await axiosInstance.get(
          API_ENDPOINTS.schoolFeatures(school._id),
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        if (settingsResponse.data.status === "success") {
          const settings = settingsResponse.data.data
          setAttendanceRadius(settings.attendanceRadius || 1000)
          setStudentBusTrackingEnabled(settings.enableStudentBusService?.enabled ?? true)
        }
      }
    } catch (error) {
      console.error("Error fetching school data:", error)
      setError("Failed to load attendance settings")
      toast.error("Failed to load attendance settings")
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!schoolId) {
      toast.error("School ID not found")
      return
    }

    const radius = Number(attendanceRadius)
    
    if (isNaN(radius) || radius < 0 || radius > 10000) {
      setError("Radius must be between 0 and 10000 meters")
      toast.error("Radius must be between 0 and 10000 meters")
      return
    }

    try {
      setSaving(true)
      setError("")
      
      const token = localStorage.getItem("token")
      
      // First get current settings to preserve other fields
      const getResponse = await axiosInstance.get(
        API_ENDPOINTS.schoolFeatures(schoolId),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (getResponse.data.status === "success") {
        const currentSettings = getResponse.data.data
        
        // Update only attendanceRadius
        const updateResponse = await axiosInstance.patch(
          API_ENDPOINTS.schoolFeatures(schoolId),
          {
            ...currentSettings,
            attendanceRadius: radius,
            enableStudentBusService: { enabled: studentBusTrackingEnabled }
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )

        if (updateResponse.data.status === "success") {
          toast.success("Settings updated successfully")
          setAttendanceRadius(radius)
        }
      }
    } catch (error) {
      console.error("Error updating attendance radius:", error)
      const errorMsg = error.response?.data?.message || "Failed to update settings"
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MapPin className="h-6 w-6 text-[#0263cc]" />
            </div>
            <div>
              <CardTitle className="text-2xl">Attendance Radius Settings</CardTitle>
              <CardDescription>
                Configure the maximum distance (in meters) from school location for clock-in
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-base font-semibold">
                Student Bus Tracking Visibility
              </Label>
              <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">Show bus tracking to students</p>
                  <p className="text-xs text-gray-500 mt-1">
                    School admin can enable or hide bus tracking only for student accounts.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={studentBusTrackingEnabled}
                  onChange={(e) => setStudentBusTrackingEnabled(e.target.checked)}
                  className="w-4 h-4 text-[#0263cc] bg-gray-100 border-gray-300 rounded focus:ring-[#0263cc]"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="radius" className="text-base font-semibold">
                Attendance Radius (meters)
              </Label>
              <Input
                id="radius"
                type="number"
                min="0"
                max="10000"
                value={attendanceRadius}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === "" || (!isNaN(value) && Number(value) >= 0 && Number(value) <= 10000)) {
                    setAttendanceRadius(value === "" ? "" : Number(value))
                    setError("")
                  }
                }}
                placeholder="Enter radius in meters"
                className="text-lg"
              />
              <p className="text-sm text-gray-500">
                Teachers can clock in if they are within this distance from the school location.
                Range: 0 - 10,000 meters (0 - 10 km)
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How it works:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Default radius is 1000 meters (1 km)</li>
                  <li>Teachers must be within this radius to clock in</li>
                  <li>If outside the radius, teachers will see a warning but can still force mark attendance</li>
                  <li>The system calculates distance using GPS coordinates</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSave}
                disabled={saving || !attendanceRadius || attendanceRadius < 0 || attendanceRadius > 10000}
                className="bg-[#0263cc] text-white hover:bg-[#0263cc]/90 min-w-[120px]"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

